import React from "react";
import {useCsContext} from "../../state/CsInstance";
import {observer} from "mobx-react-lite";
import CompileButton from "../CompileButton/CompileButton";
import ContextMenu from "../ContextMenu";
// Frontend
import styles from "./Editor.module.scss";
import {ErrorBoundary} from "react-error-boundary";
import ErrorComponent from "../ErrorComponent/ErrorComponent";
// CodeMirror
import {EditorView} from "@codemirror/view";

type Props = {};

/**
 * Vanilla CodeMirror principles:
 *  - Only apply editor actions when codeStore calls for it.
 *  - In general, move away from React.  Make actions into functions, and call
 *    them explicitly.
 */
const EditorWrapper: React.FC<Props> = (props: Props) => {
	const csInstance = useCsContext();
	const {codeStore, featureFlagStore} = csInstance;

	const editorRef = React.useRef<HTMLDivElement>(null);

	// On render, create and add the editor view to the DOM, then have codeStore
	// properly update the editor content.
	const hasRendered = React.useRef(false);
	React.useEffect(() => {
		if (editorRef.current && hasRendered.current === false) {
			const view = new EditorView({});
			editorRef.current.appendChild(view.dom);
			codeStore.codeMirrorStore.codemirrorView = view;
			// As soon as the editor is rendered, update the editor content
			codeStore.codeMirrorStore.updateEditorContentFromFocusedFile();

			hasRendered.current = true;
		}
		// We ONLY want this to run once, so don't add extra dependencies
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editorRef.current]);

	return (
		<ErrorBoundary FallbackComponent={ErrorComponent}>
			<ContextMenu />
			<div className={styles.editorToolbar}>
				{featureFlagStore.showExtraButtons && (
					<button
						className={styles.resetButton}
						onClick={codeStore.resetProject}
					>
						Reset
					</button>
				)}
				{!featureFlagStore.hideCompileButton && (
					<CompileButton disabled={!codeStore.hasUncompiledChanges} />
				)}
			</div>
			<div id="editor" ref={editorRef} className={styles.editor} />
		</ErrorBoundary>
	);
};

export default observer(EditorWrapper);
